'use client';

import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { colors, formatNum, baseStyles } from './SharedUI';
// IMPORT HOOK QUẢN LÝ MẠNG TOÀN CỤC
import { useBinanceStream } from './useBinanceStream';

const localStyles = {
  btn: { backgroundColor: colors.bg, color: colors.textMain, padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', transition: 'all 0.2s' }
};

const SupportResistanceRadarV3 = forwardRef(({ symbol, liveDataRef, smartMoneyRef, onZonesUpdate }, ref) => {
  // ==========================================
  // STATE: BẢN NHÁP (UI CHỜ LƯU)
  // ==========================================
  const [selectedDays, setSelectedDays] = useState(30); 
  const [pendingAuto, setPendingAuto] = useState(false); 

  // ==========================================
  // STATE: HỆ THỐNG ĐANG CHẠY THỰC TẾ (ACTIVE)
  // ==========================================
  const [activeDays, setActiveDays] = useState(30); 
  const [isAuto, setIsAuto] = useState(false); 
  
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [macroData, setMacroData] = useState(null);
  const [liveScan, setLiveScan] = useState(null);
  const [showDetails, setShowDetails] = useState(true);

  // ==========================================
  // CÔNG TẮC BẬT/TẮT MẮT THẦN
  // ==========================================
  const [enableMicro, setEnableMicro] = useState(false);
  const [isMicroActive, setIsMicroActive] = useState(false);
  const [microMetrics, setMicroMetrics] = useState(null);
  
  // SỬ DỤNG HOOK DÙNG CHUNG ĐỂ LẤY SHARED STREAM DATA
  const { streamData, wsStatus, requestServerSubList } = useBinanceStream(symbol?.toLowerCase(), isMicroActive);
  
  const [serverSubscriptions, setServerSubscriptions] = useState(null);
  const microIntervalRef = useRef(null);
  const lastProcessedPriceRef = useRef(0); // [MỚI] Ref để theo dõi chống re-render thừa

  // HÀM DỪNG SOI (Chỉ cần set isMicroActive = false, Hook sẽ tự lo ngắt mạng)
  const stopMicroStream = useCallback(() => {
    setIsMicroActive(false);
    if (microIntervalRef.current) clearInterval(microIntervalRef.current);
    setMicroMetrics(null);
    setServerSubscriptions(null); 
  }, []);

  // Tạo Két sắt giữ data mới nhất
  const latestDataRef = useRef({ liveScan: null, microMetrics: null, macroData: null });

  // Liên tục cập nhật Két sắt mỗi khi State thay đổi
  useEffect(() => {
    latestDataRef.current = { liveScan, microMetrics, macroData };
  }, [liveScan, microMetrics, macroData]);

  // Ống hút chỉ việc thò vào Két sắt lấy đồ
  useImperativeHandle(ref, () => ({
    getSnapshot: () => latestDataRef.current
  }));

  // HÀM BẬT SOI MẮT THẦN (Dùng data từ Hook)
  const startMicroStream = useCallback(() => {
    if (!symbol) return;
    
    setIsMicroActive(true);

    // Throttle Update UI Mắt thần mỗi 500ms
    if (microIntervalRef.current) clearInterval(microIntervalRef.current);
    microIntervalRef.current = setInterval(() => {
      const d = streamData; 
      if (d.bids.length === 0 || d.asks.length === 0) return;
      
      let tBids = 0; d.bids.forEach(b => tBids += parseFloat(b[1]));
      let tAsks = 0; d.asks.forEach(a => tAsks += parseFloat(a[1]));
      let imb = tBids > 0 ? (tAsks / tBids) : 0;
      
      setMicroMetrics({ totalBids: tBids, totalAsks: tAsks, cvd: d.cvd, imbalance: imb });
    }, 500);

  }, [symbol, streamData]);

  const handleCheckSubscriptions = () => {
    setServerSubscriptions(['⏳ Đang lấy data từ Server...']);
    requestServerSubList();
    setTimeout(() => {
        setServerSubscriptions(['✅ Đã gửi yêu cầu, vui lòng xem log bên bảng Radar (nếu có).']);
    }, 1000);
  };

  // Xử lý sự kiện Component Unmount
  useEffect(() => {
    return () => {
      stopMicroStream();
    };
  }, [stopMicroStream]);

  // Xử lý sự kiện bật/tắt thủ công Mắt thần (Đã tách logic gọi socket ra khỏi hàm Quét Giá)
  useEffect(() => {
    if (!enableMicro) {
      stopMicroStream();
    } else {
      startMicroStream();
      if (macroData) scanLiveGPS(macroData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMicro]);

  // ==========================================
  // 1. TẢI & TÍNH TOÁN DỮ LIỆU VĨ MÔ
  // ==========================================
  const fetchMacroData = async (days) => {
    if (!symbol) return;
    setLoading(true);
    try {
      const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol.toUpperCase()}&interval=1d&limit=${days}`);
      const klines = await res.json();

      if (!klines || klines.length === 0) return;

      const highs = klines.map(k => parseFloat(k[2]));
      const lows = klines.map(k => parseFloat(k[3]));
      const closes = klines.map(k => parseFloat(k[4]));

      const maxHigh = Math.max(...highs);
      const minLow = Math.min(...lows);
      const maxClose = Math.max(...closes);
      const minClose = Math.min(...closes);
      const range = maxHigh - minLow;

      const f786 = minLow + range * 0.786;
      const f618 = minLow + range * 0.618;
      const f500 = minLow + range * 0.5;
      const f382 = minLow + range * 0.382;
      const f236 = minLow + range * 0.236;

      const q75 = minLow + range * 0.75;
      const q25 = minLow + range * 0.25;

      const sellLower = Math.min(f786, q75, maxClose);
      const sellUpper = maxHigh;

      const buyLower = minLow;
      const buyUpper = Math.max(f236, q25, minClose);

      const pivotLower = f500 - (range * 0.015);
      const pivotUpper = f500 + (range * 0.015);

      const computedMacro = {
        maxHigh, minLow, maxClose, minClose, range,
        f786, f618, f500, f382, f236,
        q75, q25,
        zones: {
          sell: { lower: sellLower, upper: sellUpper },
          pivot: { lower: pivotLower, upper: pivotUpper },
          buy: { lower: buyLower, upper: buyUpper }
        }
      };

      setMacroData(computedMacro);
      setActiveDays(days);
      scanLiveGPS(computedMacro);

      // Bắn tọa độ Cản/Đỡ ra ngoài cho thằng Cha bắt lấy
      if (onZonesUpdate) {
          onZonesUpdate(computedMacro.zones);
      }

    } catch (error) {
      console.error("Lỗi fetch Klines:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = () => {
    setIsAuto(pendingAuto); 
    fetchMacroData(selectedDays); 
  };

  // ==========================================
  // 2. QUÉT TỌA ĐỘ LIVE (GPS) VÀ TÍNH R:R
  // ==========================================
  const scanLiveGPS = useCallback((mData = macroData, overridePrice = null) => {
    if (!mData) return;
    
    // [MỚI] Ưu tiên lấy overridePrice (từ Fast Ticker), nếu không có thì lấy streamData.price, cuối cùng fallback về REST API.
    const livePrice = overridePrice || parseFloat(streamData.price > 0 ? streamData.price : (liveDataRef?.current?.last || 0));
    
    if (!livePrice) return;

    const { zones } = mData;

    const whaleLongEntry = smartMoneyRef?.current?.longWhalesAvgEntryPrice ? parseFloat(smartMoneyRef.current.longWhalesAvgEntryPrice) : 0;
    const whaleShortEntry = smartMoneyRef?.current?.shortWhalesAvgEntryPrice ? parseFloat(smartMoneyRef.current.shortWhalesAvgEntryPrice) : 0;

    let sellStars = 3;
    let buyStars = 3;
    let sellConfluenceMsg = "🔥 Uy tín: [⭐⭐⭐] 3/3 (Fibo 0.786 + Quarter 75% + Đỉnh Nến)";
    let buyConfluenceMsg = "🔥 Uy tín: [⭐⭐⭐] 3/3 (Fibo 0.236 + Quarter 25% + Đáy Nến)";

    if (whaleShortEntry >= zones.sell.lower && whaleShortEntry <= zones.sell.upper) {
      sellStars = 5;
      sellConfluenceMsg = "💎 CẢN KIM CƯƠNG: [⭐⭐⭐⭐⭐] 5/5 (Trùng Khớp Giá Cá Mập SHORT)";
    }
    if (whaleLongEntry >= zones.buy.lower && whaleLongEntry <= zones.buy.upper) {
      buyStars = 5;
      buyConfluenceMsg = "💎 ĐỠ KIM CƯƠNG: [⭐⭐⭐⭐⭐] 5/5 (Trùng Khớp Giá Cá Mập LONG)";
    }

    const distToSell = Math.max(0, zones.sell.lower - livePrice);
    const pctToSell = (distToSell / livePrice) * 100;

    const distToBuy = Math.max(0, livePrice - zones.buy.upper);
    const pctToBuy = (distToBuy / livePrice) * 100;

    let rrRatio = 0;
    if (distToBuy > 0) rrRatio = distToSell / distToBuy;
    else rrRatio = 99; 

    let positionStr = "⚠️ LƯNG CHỪNG ĐÈO (Mid-Range No Man's Land)";
    let actionStr = "🚫 Đứng ngoài (No Trade Zone). Chờ giá chạy về Hỗ trợ/Kháng cự rồi mới xét.";
    let rrDesc = `1 : ${rrRatio.toFixed(2)} (Rủi ro cao. Lời < Lỗ)`;

    const inSellZone = livePrice >= zones.sell.lower;
    const inBuyZone = livePrice <= zones.buy.upper;

    if (rrRatio > 1.5) rrDesc = `1 : ${rrRatio.toFixed(2)} (Khá Tốt. Ưu tiên LONG)`;
    if (rrRatio > 2.5) rrDesc = `1 : ${rrRatio.toFixed(2)} (QUÁ NGON. Setup chuẩn bài Quant)`;
    if (rrRatio < 0.5) rrDesc = `1 : ${rrRatio.toFixed(2)} (CỰC RỦI RO. Kháng cự quá gần)`;

    if (inSellZone) {
      positionStr = "🧱 ĐANG TRONG VÙNG KHÁNG CỰ (Sell Zone)";
      actionStr = enableMicro ? "🩸 TÌM ĐIỂM SHORT! Đang bật Mắt Thần X-Quang soi Sổ Lệnh..." : "🩸 TÌM ĐIỂM SHORT! Khuyến nghị Bật Mắt thần (WebSocket) để soi Cản.";
      rrDesc = `1 : ${rrRatio.toFixed(2)} (Biên độ Tăng đã cạn. Ưu tiên SHORT)`;
    } else if (inBuyZone) {
      positionStr = "🛡️ ĐANG TRONG VÙNG HỖ TRỢ (Buy Zone)";
      actionStr = enableMicro ? "🚀 TÌM ĐIỂM LONG! Đang bật Mắt Thần X-Quang soi Sổ Lệnh..." : "🚀 TÌM ĐIỂM LONG! Khuyến nghị Bật Mắt thần (WebSocket) để soi Đỡ.";
      rrDesc = `1 : ${rrRatio.toFixed(2)} (Biên độ Giảm đã cạn. Ưu tiên LONG)`;
    } else if (livePrice >= zones.pivot.lower && livePrice <= zones.pivot.upper) {
      positionStr = "🧲 ĐANG MẮC KẸT TẠI TRỤC XOAY (Pivot)";
      actionStr = "⚖️ GIẰNG CO! Thường sẽ có rũ hàng hai đầu. Quan sát kỹ dòng tiền.";
    }

    setLiveScan({
      livePrice, distToSell, pctToSell, distToBuy, pctToBuy, rrRatio, rrDesc, positionStr, actionStr,
      sellConfluenceMsg, buyConfluenceMsg, inSellZone, inBuyZone, sellStars, buyStars
    });
    
    setLastUpdate(new Date().toLocaleTimeString('vi-VN'));
    
  }, [macroData, liveDataRef, smartMoneyRef, enableMicro, streamData]); // Lược bỏ hàm bật/tắt mạng khỏi mảng dependency để sạch logic

  // ==========================================
  // [MỚI] TICKER CHẠY NGẦM THEO DÕI GIÁ LIVE SIÊU TỐC
  // ==========================================
  useEffect(() => {
    const ticker = setInterval(() => {
      // Ưu tiên Giá WebSocket (nhanh nhất), nếu WebSoket đang tắt thì lấy giá Ref của REST API
      const currentP = parseFloat(streamData.price > 0 ? streamData.price : (liveDataRef?.current?.last || 0));
      
      if (currentP > 0 && currentP !== lastProcessedPriceRef.current) {
        lastProcessedPriceRef.current = currentP;
        if (macroData) {
          scanLiveGPS(macroData, currentP); // Gửi giá mới vào hàm quét để cập nhật toàn bộ UI
        }
      }
    }, 300); // Tốc độ quét 300ms là mức lý tưởng để mắt người nhìn không bị giật mà vẫn rất mượt

    return () => clearInterval(ticker);
  }, [macroData, scanLiveGPS, streamData, liveDataRef]);

  // Logic Auto 15s (Vẫn giữ lại để định kỳ ép gọi API cập nhật macro nếu cần)
  useEffect(() => {
    let intervalId;
    if (isAuto && macroData) {
      intervalId = setInterval(() => { scanLiveGPS(); }, 15000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [isAuto, macroData, scanLiveGPS]);

  useEffect(() => {
    fetchMacroData(activeDays);
    setIsAuto(false);
    setPendingAuto(false);
  }, [symbol]);

  // ==========================================
  // HELPER TÍNH TOÁN & RENDER SORTED LIST CÓ % LỆCH
  // ==========================================
  const buildSortedList = (items, livePrice) => {
    const list = [...items, { label: '🔴 GIÁ LIVE HIỆN TẠI', val: livePrice, isLive: true }];
    return list.sort((a, b) => b.val - a.val); 
  };

  const renderSortedList = (sortedArray) => {
    return (
      <ul style={{ paddingLeft: '0', margin: '0', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {sortedArray.map((item, idx) => {
          const decimals = item.val < 1 ? 6 : 4; 
          
          if (item.isLive) {
            const upperItem = idx > 0 ? sortedArray[idx - 1] : null;
            const lowerItem = idx < sortedArray.length - 1 ? sortedArray[idx + 1] : null;

            return (
              <li key={idx} style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', padding: '8px 12px', borderRadius: '6px', border: `1px solid ${colors.red}`, display: 'flex', flexDirection: 'column', gap: '6px', transition: 'all 0.3s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', color: colors.red }}>
                  <span>{item.label}</span>
                  <span>${formatNum(item.val, decimals)}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#e2e8f0', borderTop: '1px dashed rgba(239, 68, 68, 0.4)', paddingTop: '6px' }}>
                  <span>
                    {upperItem 
                      ? `👇 Cách mốc trên: -${(((upperItem.val - item.val) / item.val) * 100).toFixed(2)}%` 
                      : '🚀 Vượt mọi đỉnh!'}
                  </span>
                  <span>
                    {lowerItem 
                      ? `👆 Cách mốc dưới: +${(((item.val - lowerItem.val) / item.val) * 100).toFixed(2)}%` 
                      : '🩸 Thủng mọi đáy!'}
                  </span>
                </div>
              </li>
            );
          }
          
          return (
            <li key={idx} style={{ color: colors.textSub, padding: '4px 12px', display: 'flex', justifyContent: 'space-between' }}>
              <span>{item.label}</span>
              <strong style={{ color: colors.textMain }}>${formatNum(item.val, decimals)}</strong>
            </li>
          );
        })}
      </ul>
    );
  };

  // ==========================================
  // 3. UI COMPONENTS
  // ==========================================
  const renderHeatmap = () => {
    if (!macroData || !liveScan) return null;
    const { minLow, range, zones } = macroData;
    const lp = liveScan.livePrice;

    const getPct = (val) => Math.max(0, Math.min(100, ((val - minLow) / range) * 100));

    const buyL = getPct(zones.buy.lower); const buyW = getPct(zones.buy.upper) - buyL;
    const pivL = getPct(zones.pivot.lower); const pivW = getPct(zones.pivot.upper) - pivL;
    const selL = getPct(zones.sell.lower); const selW = getPct(zones.sell.upper) - selL;
    const livePct = getPct(lp);

    const decimals = lp < 1 ? 6 : 4;

    return (
      <div style={{ padding: '10px 0', marginTop: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub, marginBottom: '6px' }}>
          <span>Wick Low: ${formatNum(minLow, decimals)}</span>
          <span>Wick High: ${formatNum(macroData.maxHigh, decimals)}</span>
        </div>
        <div style={{ position: 'relative', height: '14px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
          <div style={{ position: 'absolute', left: `${buyL}%`, width: `${buyW}%`, height: '100%', backgroundColor: colors.green, opacity: 0.8, borderRadius: '6px' }} title="Buy Zone"></div>
          <div style={{ position: 'absolute', left: `${pivL}%`, width: `${pivW}%`, height: '100%', backgroundColor: colors.warn, opacity: 0.8 }} title="Pivot Zone"></div>
          <div style={{ position: 'absolute', left: `${selL}%`, width: `${selW}%`, height: '100%', backgroundColor: colors.red, opacity: 0.8, borderRadius: '6px' }} title="Sell Zone"></div>
          
          {/* CỤC ĐỊNH VỊ GIÁ LIVE TRÊN HEATMAP CŨNG ĐƯỢC CHẠY THEO REALTIME */}
          <div style={{ position: 'absolute', left: `calc(${livePct}% - 7px)`, top: '-4px', width: '14px', height: '22px', backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 0 10px rgba(255,255,255,0.5)', zIndex: 10, border: `2px solid ${colors.bg}`, transition: 'left 0.3s ease-out' }} title={`Live: ${lp}`}></div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#fff', marginTop: '8px', fontWeight: 'bold' }}>
          Giá Trục: 🔴 ${formatNum(lp, decimals)}
        </div>
      </div>
    );
  };

  const getDecimals = (val) => val < 1 ? 6 : 4;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
      <div style={baseStyles.card}>
        
        {/* HEADER VÀ CẤU HÌNH */}
        <div style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <strong style={{ color: colors.textMain, fontSize: '16px' }}>🎯 HỆ THỐNG RADAR TÌM VÙNG CẢN HỢP LƯU V3 (LƯỢNG TỬ & VI MÔ)</strong>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'center' }}>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '8px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: `1px dashed ${colors.border}` }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Khung soi Vĩ mô:</span>
              
              <select value={selectedDays} onChange={(e) => setSelectedDays(Number(e.target.value))} style={baseStyles.select}>
                <option value={7}>7 Ngày</option>
                <option value={15}>15 Ngày</option>
                <option value={30}>30 Ngày (Chuẩn)</option>
                <option value={45}>45 Ngày</option>
              </select>
              
              <div style={{ width: '1px', height: '16px', backgroundColor: colors.border, margin: '0 4px' }}></div>
              
              <div style={{ display: 'flex', backgroundColor: colors.bg, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                <button 
                  onClick={() => setPendingAuto(false)} 
                  style={{ padding: '4px 10px', fontSize: '12px', border: 'none', background: !pendingAuto ? 'rgba(255,255,255,0.1)' : 'transparent', color: !pendingAuto ? '#fff' : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}
                >MANUAL</button>
                <button 
                  onClick={() => setPendingAuto(true)} 
                  style={{ padding: '4px 10px', fontSize: '12px', border: 'none', background: pendingAuto ? colors.greenBg : 'transparent', color: pendingAuto ? colors.green : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}
                >AUTO 15s</button>
              </div>

              <button 
                onClick={handleSaveConfig} disabled={loading}
                style={{ ...localStyles.btn, backgroundColor: colors.bg, border: `1px solid ${colors.textMain}`, marginLeft: '8px' }}
              >
                {loading ? '⏳...' : '💾 LƯU & ÁP DỤNG'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button 
                onClick={() => scanLiveGPS()} 
                style={{ ...localStyles.btn, border: `1px solid ${colors.warn}`, color: colors.warn, backgroundColor: 'transparent' }}
              >
                ⚡ QUÉT TỌA ĐỘ LIVE NAY
              </button>
            </div>

          </div>
          
          <div style={{ fontSize: '11px', color: colors.textSub, marginTop: '8px', textAlign: 'right' }}>
            Trạng thái máy chủ: Khung <b>{activeDays} Ngày</b> | Chế độ: <strong style={{color: isAuto ? colors.green : colors.textMain}}>{isAuto ? '🟢 AUTO' : '🔘 MANUAL'}</strong> | Cập nhật Live: {lastUpdate || 'Chưa có'}
          </div>
        </div>

        {/* BẢNG ĐIỀU KHIỂN SAU KHI CÓ DATA */}
        {macroData && liveScan && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* 📡 ĐỊNH VỊ TOẠ ĐỘ LIVE (GPS) */}
            <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ fontWeight: 'bold', color: '#3b82f6', fontSize: '14px', marginTop: '4px' }}>📡 ĐỊNH VỊ TOẠ ĐỘ LIVE (GPS)</div>
                
                {/* NÚT BẬT/TẮT MẮT THẦN & KIỂM TRA LUỒNG */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <button
                    onClick={() => setEnableMicro(!enableMicro)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${enableMicro ? colors.green : colors.border}`,
                      color: enableMicro ? colors.green : colors.textSub,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ width: '6px', height: '6px', backgroundColor: isMicroActive ? colors.green : (enableMicro ? colors.warn : colors.border), borderRadius: '50%', animation: isMicroActive ? 'pulse 1.5s infinite' : 'none' }}></div>
                    MẮT THẦN (100MS): {enableMicro ? (isMicroActive ? 'ĐANG SOI' : 'ĐANG CANH ZONE') : 'ĐÃ TẮT'}
                  </button>
                  
                  {enableMicro && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', marginTop: '4px' }}>
                          <div style={{ fontSize: '10px', color: wsStatus.color, fontWeight: 'bold' }}>
                              Trạng thái: {wsStatus.text}
                          </div>
                          <button
                            onClick={handleCheckSubscriptions}
                            style={{ background: 'transparent', border: `1px solid ${colors.textMain}`, color: colors.textMain, padding: '2px 8px', borderRadius: '4px', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            🔍 KIỂM TRA LUỒNG TRÊN SERVER
                          </button>
                          
                          {/* HIỂN THỊ KẾT QUẢ TỪ SERVER */}
                          {serverSubscriptions && (
                            <div style={{ fontSize: '10px', color: colors.green, backgroundColor: 'rgba(0,0,0,0.5)', border: `1px dashed ${colors.green}`, padding: '4px 8px', borderRadius: '4px', textAlign: 'right', maxWidth: '250px', wordWrap: 'break-word', marginTop: '2px' }}>
                              {serverSubscriptions.join(', ')}
                            </div>
                          )}
                      </div>
                  )}
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', fontSize: '13px' }}>
                <div><span style={{ color: colors.textSub }}>🔴 Giá Live Hiện Tại:</span> <strong style={{ fontSize: '16px', color: colors.textMain, transition: 'color 0.3s' }}>${formatNum(liveScan.livePrice, getDecimals(liveScan.livePrice))}</strong></div>
                <div><span style={{ color: colors.textSub }}>📍 Vị thế:</span> <strong style={{ color: colors.warn }}>{liveScan.positionStr}</strong></div>
                <div><span style={{ color: colors.textSub }}>⚖️ Lệch pha R:R:</span> <strong style={{ color: liveScan.rrRatio > 1.5 ? colors.green : (liveScan.rrRatio < 0.5 ? colors.red : colors.warn) }}>{liveScan.rrDesc}</strong></div>
              </div>
              {renderHeatmap()}
            </div>

            {/* 🎯 VÙNG NGẮM BẮN HỢP LƯU */}
            <div>
              <div style={{ fontWeight: 'bold', color: colors.textMain, fontSize: '14px', marginBottom: '12px', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px' }}>
                🎯 VÙNG NGẮM BẮN HỢP LƯU (CONFLUENCE ZONES)
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                <div style={{ backgroundColor: colors.redBg, borderLeft: `4px solid ${colors.red}`, padding: '12px', borderRadius: '0 8px 8px 0', border: liveScan.inSellZone ? `1px solid ${colors.red}` : 'none' }}>
                  <div style={{ fontWeight: 'bold', color: colors.red, fontSize: '13px', marginBottom: '4px' }}>🧱 VÙNG KHÁNG CỰ (SELL ZONE) : [ ${formatNum(macroData.zones.sell.lower, getDecimals(macroData.zones.sell.lower))} - ${formatNum(macroData.zones.sell.upper, getDecimals(macroData.zones.sell.upper))} ]</div>
                  <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                    <div style={{ color: liveScan.sellStars === 5 ? colors.warn : colors.textSub, fontWeight: liveScan.sellStars === 5 ? 'bold' : 'normal' }}>{liveScan.sellConfluenceMsg}</div>
                    <div>📏 Khoảng cách: Cách Live <strong style={{ color: colors.textMain }}>${formatNum(liveScan.distToSell, getDecimals(liveScan.distToSell))} 📈 (Tăng +{liveScan.pctToSell.toFixed(2)}%)</strong></div>
                    <div>⏱️ Trạng thái: {liveScan.pctToSell < 2 && !liveScan.inSellZone ? <span style={{color: colors.warn}}>🚨 Sắp chạm Cản!</span> : "An toàn."}</div>
                  </div>
                  
                  {isMicroActive && liveScan.inSellZone && microMetrics && (
                    <div style={{ marginTop: '12px', padding: '10px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '6px', border: `1px dashed ${colors.red}` }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.textMain, marginBottom: '6px' }}>👁️ XÁC NHẬN SỔ LỆNH LIVE (100ms):</div>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                        <span>Tường Đè (Ask/Bid): <strong style={{ color: microMetrics.imbalance > 1.5 ? colors.red : colors.warn }}>{microMetrics.imbalance.toFixed(2)}x</strong></span>
                        <span>Áp lực Market (CVD): <strong style={{ color: microMetrics.cvd < 0 ? colors.red : colors.green }}>{microMetrics.cvd > 0 ? '+' : ''}{microMetrics.cvd.toFixed(2)}</strong></span>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: 'bold', color: (microMetrics.imbalance >= 2 && microMetrics.cvd < 0) ? colors.red : colors.warn }}>
                        {(microMetrics.imbalance >= 2 && microMetrics.cvd < 0) 
                          ? "🚨 XÁC NHẬN: CÁ MẬP XẢ ĐÚNG CẢN! BÓP CÒ SHORT NGAY!" 
                          : "⚠️ Động lượng xả chưa rõ ràng (Đề phòng đục cản). Chờ X-quang..."}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ backgroundColor: colors.warnBg, borderLeft: `4px solid ${colors.warn}`, padding: '12px', borderRadius: '0 8px 8px 0' }}>
                  <div style={{ fontWeight: 'bold', color: colors.warn, fontSize: '13px', marginBottom: '4px' }}>🧲 TRỤC XOAY (PIVOT POINT) : [ ${formatNum(macroData.zones.pivot.lower, getDecimals(macroData.zones.pivot.lower))} - ${formatNum(macroData.zones.pivot.upper, getDecimals(macroData.zones.pivot.upper))} ]</div>
                  <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                    <div>⚠️ Uy tín: [⭐⭐] 2/3 (Fibo 0.5 + Quarter 50%)</div>
                    <div>📏 Khoảng cách Tới Trục: <strong style={{ color: colors.textMain }}>${formatNum(Math.abs(liveScan.livePrice - macroData.f500), getDecimals(Math.abs(liveScan.livePrice - macroData.f500)))} (Lệch {Math.abs(((liveScan.livePrice - macroData.f500)/liveScan.livePrice)*100).toFixed(2)}%)</strong></div>
                  </div>
                </div>

                <div style={{ backgroundColor: colors.greenBg, borderLeft: `4px solid ${colors.green}`, padding: '12px', borderRadius: '0 8px 8px 0', border: liveScan.inBuyZone ? `1px solid ${colors.green}` : 'none' }}>
                  <div style={{ fontWeight: 'bold', color: colors.green, fontSize: '13px', marginBottom: '4px' }}>🛡️ VÙNG HỖ TRỢ CỨNG (BUY ZONE) : [ ${formatNum(macroData.zones.buy.lower, getDecimals(macroData.zones.buy.lower))} - ${formatNum(macroData.zones.buy.upper, getDecimals(macroData.zones.buy.upper))} ]</div>
                  <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                    <div style={{ color: liveScan.buyStars === 5 ? colors.warn : colors.textSub, fontWeight: liveScan.buyStars === 5 ? 'bold' : 'normal' }}>{liveScan.buyConfluenceMsg}</div>
                    <div>📏 Khoảng cách: Cách Live <strong style={{ color: colors.textMain }}>${formatNum(liveScan.distToBuy, getDecimals(liveScan.distToBuy))} 📉 (Giảm -{liveScan.pctToBuy.toFixed(2)}%)</strong></div>
                    <div>⏱️ Trạng thái: {liveScan.pctToBuy < 2 && !liveScan.inBuyZone ? <span style={{color: colors.warn}}>🚨 Sắp chạm Đáy!</span> : "Còn xa hỗ trợ."}</div>
                  </div>

                  {isMicroActive && liveScan.inBuyZone && microMetrics && (
                    <div style={{ marginTop: '12px', padding: '10px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '6px', border: `1px dashed ${colors.green}` }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.textMain, marginBottom: '6px' }}>👁️ XÁC NHẬN SỔ LỆNH LIVE (100ms):</div>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                        <span>Tường Đỡ (Bid/Ask): <strong style={{ color: (microMetrics.totalBids / (microMetrics.totalAsks||1)) > 1.5 ? colors.green : colors.warn }}>{((microMetrics.totalBids / (microMetrics.totalAsks||1))).toFixed(2)}x</strong></span>
                        <span>Áp lực Market (CVD): <strong style={{ color: microMetrics.cvd > 0 ? colors.green : colors.red }}>{microMetrics.cvd > 0 ? '+' : ''}{microMetrics.cvd.toFixed(2)}</strong></span>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: 'bold', color: ((microMetrics.totalBids / (microMetrics.totalAsks||1)) >= 2 && microMetrics.cvd > 0) ? colors.green : colors.warn }}>
                        {((microMetrics.totalBids / (microMetrics.totalAsks||1)) >= 2 && microMetrics.cvd > 0) 
                          ? "🚨 XÁC NHẬN: CÁ MẬP GOM ĐÚNG ĐÁY! BÓP CÒ LONG NGAY!" 
                          : "⚠️ Lực đỡ chưa rõ ràng (Đề phòng thủng đáy). Chờ X-quang..."}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* 🧠 BỘ NÃO KHUYẾN NGHỊ LỆNH */}
            <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '14px', color: colors.textMain, marginBottom: '8px', fontWeight: 'bold' }}>🧠 AI TRADER KHUYẾN NGHỊ (Dựa trên GPS & Mắt Thần):</div>
              <div style={{ fontSize: '13px', color: colors.textSub, lineHeight: '1.6' }}>
                👉 TÌNH HÌNH: {liveScan.positionStr}.<br/>
                👉 KẾ HOẠCH TÁC CHIẾN: <strong style={{ color: liveScan.rrRatio > 1.5 ? colors.green : (liveScan.rrRatio < 0.5 ? colors.red : colors.warn) }}>{liveScan.actionStr}</strong><br/>
                <em>*Khi được BẬT, hệ thống chỉ tự động mở luồng Mắt Thần 100ms khi giá tiến vào Kill Zone để xác nhận dòng tiền.</em>
              </div>
            </div>

            <button 
              onClick={() => setShowDetails(!showDetails)}
              style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '12px', textAlign: 'center', marginTop: '8px', padding: '10px', borderTop: `1px dashed rgba(255,255,255,0.1)` }}
            >
              {showDetails ? '🔼 ẨN CHI TIẾT 3 LỚP PHÂN TÍCH' : '🔽 MỞ BẢNG CHẨN ĐOÁN LIVE THEO 3 LỚP'}
            </button>

            {/* BẢNG CHẨN ĐOÁN LIVE 3 LỚP */}
            {showDetails && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', fontSize: '12px', border: `1px solid ${colors.border}` }}>
                
                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{color: colors.textMain, fontSize: '13px', borderBottom: `1px solid ${colors.border}`, display: 'block', paddingBottom: '8px', marginBottom: '12px' }}>📐 Lớp 1: Fibonacci Retracement</strong>
                  {renderSortedList(buildSortedList([
                    { label: '1.000 (Đỉnh)', val: macroData.maxHigh },
                    { label: '0.786', val: macroData.f786 },
                    { label: '0.618', val: macroData.f618 },
                    { label: '0.500 (Trục Xoay)', val: macroData.f500 },
                    { label: '0.382', val: macroData.f382 },
                    { label: '0.236', val: macroData.f236 },
                    { label: '0.000 (Đáy)', val: macroData.minLow }
                  ], liveScan.livePrice))}
                </div>

                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{color: colors.textMain, fontSize: '13px', borderBottom: `1px solid ${colors.border}`, display: 'block', paddingBottom: '8px', marginBottom: '12px' }}>🍕 Lớp 2: Quarter Theory (4 Phần)</strong>
                  {renderSortedList(buildSortedList([
                    { label: 'Q75 (Vùng Xả Hàng)', val: macroData.q75 },
                    { label: 'Q50 (Trục Xoay)', val: macroData.f500 },
                    { label: 'Q25 (Vùng Gom Hàng)', val: macroData.q25 }
                  ], liveScan.livePrice))}
                </div>

                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{color: colors.textMain, fontSize: '13px', borderBottom: `1px solid ${colors.border}`, display: 'block', paddingBottom: '8px', marginBottom: '12px' }}>🧱 Lớp 3: Real Body (Thân/Râu)</strong>
                  {renderSortedList(buildSortedList([
                    { label: 'Wick High (Râu Đỉnh)', val: macroData.maxHigh },
                    { label: 'Close High (Cản Thân)', val: macroData.maxClose },
                    { label: 'Close Low (Đỡ Thân)', val: macroData.minClose },
                    { label: 'Wick Low (Râu Đáy)', val: macroData.minLow }
                  ], liveScan.livePrice))}
                </div>

              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
});
export default SupportResistanceRadarV3;
